import { ContratoWithDetails, PropostaParaContrato } from '@/types/contrato';

export interface DadosContrato {
  valor_kit: number;
  valor_integracao: number;
  valor_total: number;
  // Campos PF
  nome_cliente: string;
  cpf: string;
  estado_civil: string;
  profissao: string;
  // Campos PJ
  razao_social: string;
  cnpj: string;
  representante_legal: string;
  cpf_representante: string;
  // Endereço
  endereco: string;
  numero: string;
  bairro: string;
  cidade: string;
  estado: string;
  cep: string;
  // Campos da Empresa (Contratada)
  razao_social_empresa: string;
  nome_fantasia_empresa: string;
  cnpj_empresa: string;
  endereco_empresa: string;
  numero_empresa: string;
  bairro_empresa: string;
  cidade_empresa: string;
  estado_empresa: string;
  cep_empresa: string;
  representante_legal_empresa: string;
  cpf_representante_empresa: string;
  cargo_representante_empresa: string;
  // Campos de pagamento do kit
  kit_forma_pagamento: string;
  kit_valor_pagamento: number;
  kit_numero_parcelas: number;
  kit_valor_parcela: number;
  kit_observacoes: string;
  // Campos de pagamento da integração
  integracao_modalidade_pagamento: number;
  integracao_forma_pagamento: string; // Mantido para compatibilidade
  integracao_valor_total: number;
  integracao_numero_parcelas: number;
  integracao_valor_parcela: number;
  integracao_observacoes: string;
  // Percentuais de pagamento da integração
  integracao_percentual_assinatura: number;
  integracao_percentual_inicio_obra: number;
  integracao_percentual_conclusao_obra: number;
  integracao_percentual_ligacao_definitiva: number;
  // Valores de cada etapa da integração
  integracao_valor_assinatura: number;
  integracao_valor_inicio_obra: number;
  integracao_valor_conclusao_obra: number;
  integracao_valor_ligacao_definitiva: number;
  // Formas de pagamento por etapa da integração
  integracao_forma_pagamento_assinatura: string;
  integracao_forma_pagamento_inicio_obra: string;
  integracao_forma_pagamento_conclusao_obra: string;
  integracao_forma_pagamento_ligacao_definitiva: string;
  // Parcelas por etapa da integração (quando forma de pagamento é cartão de crédito)
  integracao_numero_parcelas_assinatura: number;
  integracao_valor_parcela_assinatura: number;
  integracao_numero_parcelas_inicio_obra: number;
  integracao_valor_parcela_inicio_obra: number;
  integracao_numero_parcelas_conclusao_obra: number;
  integracao_valor_parcela_conclusao_obra: number;
  integracao_numero_parcelas_ligacao_definitiva: number;
  integracao_valor_parcela_ligacao_definitiva: number;
}

// Funções para cálculo de pagamento
export const calcularValorParcela = (valorTotal: number, numeroParcelas: number): number => {
  return numeroParcelas > 0 ? valorTotal / numeroParcelas : 0;
};

// Função para calcular valores da integração baseado na modalidade e percentuais
export const calcularValoresIntegracao = (modalidade: number, valorTotal: number, percentuais: any) => {
  const valores = {
    integracao_valor_assinatura: 0,
    integracao_valor_inicio_obra: 0,
    integracao_valor_conclusao_obra: 0,
    integracao_valor_ligacao_definitiva: 0
  };

  switch (modalidade) {
    case 1: // Integral na assinatura
      valores.integracao_valor_assinatura = valorTotal;
      break;
    case 2: // Assinatura + Conclusão
      valores.integracao_valor_assinatura = (valorTotal * percentuais.integracao_percentual_assinatura) / 100;
      valores.integracao_valor_conclusao_obra = (valorTotal * percentuais.integracao_percentual_conclusao_obra) / 100;
      break;
    case 3: // Assinatura + Início + Conclusão
      valores.integracao_valor_assinatura = (valorTotal * percentuais.integracao_percentual_assinatura) / 100;
      valores.integracao_valor_inicio_obra = (valorTotal * percentuais.integracao_percentual_inicio_obra) / 100;
      valores.integracao_valor_conclusao_obra = (valorTotal * percentuais.integracao_percentual_conclusao_obra) / 100;
      break;
    case 4: // Assinatura + Início + Conclusão + Ligação
      valores.integracao_valor_assinatura = (valorTotal * percentuais.integracao_percentual_assinatura) / 100;
      valores.integracao_valor_inicio_obra = (valorTotal * percentuais.integracao_percentual_inicio_obra) / 100;
      valores.integracao_valor_conclusao_obra = (valorTotal * percentuais.integracao_percentual_conclusao_obra) / 100;
      valores.integracao_valor_ligacao_definitiva = (valorTotal * percentuais.integracao_percentual_ligacao_definitiva) / 100;
      break;
    case 5: // Pagamento integral na conclusão da obra
      valores.integracao_valor_conclusao_obra = valorTotal;
      break;
  }

  return valores;
};

// Função para validar se a soma dos percentuais de integração é 100%
export const validarPercentuaisIntegracao = (dadosContrato: DadosContrato): { valido: boolean; mensagem?: string } => {
  const modalidade = dadosContrato.integracao_modalidade_pagamento;
  
  // Se modalidade é 1 ou 5 (pagamento integral), não precisa validar percentuais
  if (modalidade === 1 || modalidade === 5) {
    return { valido: true };
  }
  
  // Não bloquear salvamento, apenas informar se a soma não for 100%
  // A validação visual já está sendo feita no componente
  return { valido: true };
};

// Função para validar se todos os campos obrigatórios estão preenchidos
export const validarCamposObrigatorios = (
  dadosContrato: DadosContrato, 
  tipoCliente?: string
): boolean => {
  const camposObrigatorios = [
    // Campos de endereço (sempre obrigatórios)
    'endereco', 'numero', 'bairro', 'cidade', 'estado', 'cep',
    // Campos de pagamento do kit
    'kit_forma_pagamento'
  ];
  
  // Validar formas de pagamento da integração baseado na modalidade
  const modalidade = dadosContrato.integracao_modalidade_pagamento;
  if (modalidade === 1) {
    camposObrigatorios.push('integracao_forma_pagamento_assinatura');
  } else if (modalidade === 2) {
    camposObrigatorios.push('integracao_forma_pagamento_assinatura', 'integracao_forma_pagamento_conclusao_obra');
  } else if (modalidade === 3) {
    camposObrigatorios.push('integracao_forma_pagamento_assinatura', 'integracao_forma_pagamento_inicio_obra', 'integracao_forma_pagamento_conclusao_obra');
  } else if (modalidade === 4) {
    camposObrigatorios.push('integracao_forma_pagamento_assinatura', 'integracao_forma_pagamento_inicio_obra', 'integracao_forma_pagamento_conclusao_obra', 'integracao_forma_pagamento_ligacao_definitiva');
  } else if (modalidade === 5) {
    camposObrigatorios.push('integracao_forma_pagamento_conclusao_obra');
  }

  // Campos específicos por tipo de cliente
  if (tipoCliente === 'PF') {
    camposObrigatorios.push('nome_cliente', 'cpf', 'estado_civil', 'profissao');
  } else {
    camposObrigatorios.push('razao_social', 'cnpj', 'representante_legal', 'cpf_representante');
  }
  
  // Campos da empresa (sempre obrigatórios)
  camposObrigatorios.push(
    'razao_social_empresa', 
    'nome_fantasia_empresa', 
    'cnpj_empresa',
    'endereco_empresa',
    'numero_empresa',
    'bairro_empresa',
    'cidade_empresa',
    'estado_empresa',
    'cep_empresa',
    'representante_legal_empresa',
    'cpf_representante_empresa',
    'cargo_representante_empresa'
  );

  // Verificar se todos os campos obrigatórios estão preenchidos
  for (const campo of camposObrigatorios) {
    const valor = dadosContrato[campo as keyof DadosContrato];
    if (!valor || (typeof valor === 'string' && valor.trim() === '')) {
      return false;
    }
  }

  return true;
};

// Função para formatar telefone
export const formatarTelefone = (telefone: string | undefined): string => {
  if (!telefone) return 'Não informado';
  
  // Remove todos os caracteres não numéricos
  const numeroLimpo = telefone.replace(/\D/g, '');
  
  // Verifica se tem 11 dígitos (celular com DDD)
  if (numeroLimpo.length === 11) {
    return `(${numeroLimpo.slice(0, 2)})${numeroLimpo.slice(2, 7)}-${numeroLimpo.slice(7)}`;
  }
  
  // Verifica se tem 10 dígitos (fixo com DDD)
  if (numeroLimpo.length === 10) {
    return `(${numeroLimpo.slice(0, 2)})${numeroLimpo.slice(2, 6)}-${numeroLimpo.slice(6)}`;
  }
  
  // Se não tem o formato esperado, retorna o original
  return telefone;
};

// Função para obter configuração do status
export const getStatusConfig = (status: string) => {
  const statusConfig = {
    'rascunho': { label: 'Rascunho', variant: 'secondary' as const },
    'proposta_aceita': { label: 'Proposta Aceita', variant: 'default' as const },
    'contrato_gerado': { label: 'Contrato Gerado', variant: 'default' as const },
    'assinado': { label: 'Assinado', variant: 'default' as const }
  };

  return statusConfig[status as keyof typeof statusConfig] || statusConfig.rascunho;
};

// Função para criar dados iniciais do contrato
export const criarDadosIniciaisContrato = (): DadosContrato => ({
  valor_kit: 0,
  valor_integracao: 0,
  valor_total: 0,
  nome_cliente: '',
  cpf: '',
  estado_civil: '',
  profissao: '',
  razao_social: '',
  cnpj: '',
  representante_legal: '',
  cpf_representante: '',
  endereco: '',
  numero: '',
  bairro: '',
  cidade: '',
  estado: '',
  cep: '',
  // Informações da Empresa (valores padrão pré-preenchidos)
  razao_social_empresa: 'RST SERVIÇOS DE ENGENHARIA LTDA',
  nome_fantasia_empresa: 'SUN7 ENERGY',
  cnpj_empresa: '37395997000136', // CNPJ sem formatação: 37.395.997/0001-36
  endereco_empresa: 'R. Belmira Isabel Martins',
  numero_empresa: '42',
  bairro_empresa: 'Estreito',
  cidade_empresa: 'Florianópolis',
  estado_empresa: 'SC',
  cep_empresa: '88075145', // CEP sem formatação: 88075-145
  representante_legal_empresa: 'Rafael Silva Teixeira',
  cpf_representante_empresa: '08840379916', // CPF sem formatação: 088.403.799-16
  cargo_representante_empresa: 'Diretor Eng.',
  kit_forma_pagamento: '',
  kit_valor_pagamento: 0,
  kit_numero_parcelas: 1,
  kit_valor_parcela: 0,
  kit_observacoes: '',
  integracao_modalidade_pagamento: 1,
  integracao_forma_pagamento: '',
  integracao_valor_total: 0,
  integracao_numero_parcelas: 1,
  integracao_valor_parcela: 0,
  integracao_observacoes: '',
  integracao_percentual_assinatura: 100,
  integracao_percentual_inicio_obra: 0,
  integracao_percentual_conclusao_obra: 0,
  integracao_percentual_ligacao_definitiva: 0,
  integracao_valor_assinatura: 0,
  integracao_valor_inicio_obra: 0,
  integracao_valor_conclusao_obra: 0,
  integracao_valor_ligacao_definitiva: 0,
  integracao_forma_pagamento_assinatura: '',
  integracao_forma_pagamento_inicio_obra: '',
  integracao_forma_pagamento_conclusao_obra: '',
  integracao_forma_pagamento_ligacao_definitiva: '',
  integracao_numero_parcelas_assinatura: 1,
  integracao_valor_parcela_assinatura: 0,
  integracao_numero_parcelas_inicio_obra: 1,
  integracao_valor_parcela_inicio_obra: 0,
  integracao_numero_parcelas_conclusao_obra: 1,
  integracao_valor_parcela_conclusao_obra: 0,
  integracao_numero_parcelas_ligacao_definitiva: 1,
  integracao_valor_parcela_ligacao_definitiva: 0
});

// Função para preencher dados do contrato a partir de uma proposta
export const preencherDadosDeProposta = (
  proposta: PropostaParaContrato, 
  dadosAtuais: DadosContrato
): DadosContrato => ({
  ...dadosAtuais,
  valor_kit: proposta.valor_total_kits,
  valor_integracao: proposta.preco_integracao,
  valor_total: proposta.valor_total_kits + proposta.preco_integracao,
  kit_valor_pagamento: proposta.valor_total_kits,
  kit_valor_parcela: proposta.valor_total_kits, // Valor inicial igual ao valor total
  integracao_valor_total: proposta.preco_integracao,
  integracao_valor_assinatura: proposta.preco_integracao,
  integracao_valor_parcela: proposta.preco_integracao, // Valor inicial igual ao valor total
  nome_cliente: proposta.lead_nome || '',
  razao_social: proposta.lead_razao_social || '',
  endereco: '',
  numero: '',
  bairro: '',
  cidade: '',
  estado: '',
  cep: ''
});

// Função para preencher dados completos do contrato a partir de uma proposta
export const preencherDadosCompletosDeProposta = (
  proposta: PropostaParaContrato, 
  dadosAtuais: DadosContrato
): DadosContrato => ({
  ...dadosAtuais,
  nome_cliente: proposta.lead_tipo === 'PF' ? proposta.lead_nome : proposta.lead_razao_social,
  cpf: proposta.lead_cpf || '',
  razao_social: proposta.lead_razao_social || '',
  cnpj: proposta.lead_cnpj || '',
  representante_legal: proposta.lead_representante_legal || '',
  cpf_representante: proposta.lead_cpf_representante || '',
  endereco: proposta.lead_endereco || '',
  numero: proposta.lead_numero || '',
  cidade: proposta.lead_cidade || '',
  estado: proposta.lead_estado || '',
  cep: proposta.lead_cep || ''
});

// Função para preencher dados do contrato a partir de um contrato existente (para edição)
export const preencherDadosDeContratoExistente = (contrato: ContratoWithDetails): DadosContrato => ({
  valor_kit: contrato.valor_kit || 0,
  valor_integracao: contrato.valor_integracao || 0,
  valor_total: contrato.valor_total || 0,
  nome_cliente: contrato.nome_cliente || '',
  cpf: contrato.cpf || '',
  estado_civil: contrato.estado_civil || '',
  profissao: contrato.profissao || '',
  razao_social: contrato.razao_social || '',
  cnpj: contrato.cnpj || '',
  representante_legal: contrato.representante_legal || '',
  cpf_representante: contrato.cpf_representante || '',
  endereco: contrato.endereco || '',
  numero: contrato.numero || '',
  bairro: contrato.bairro || '',
  cidade: contrato.cidade || '',
  estado: contrato.estado || '',
  cep: contrato.cep || '',
  // Informações da Empresa - usa valores do contrato se existirem, senão usa valores padrão
  razao_social_empresa: contrato.razao_social_empresa || 'RST SERVIÇOS DE ENGENHARIA LTDA',
  nome_fantasia_empresa: contrato.nome_fantasia_empresa || 'SUN7 ENERGY',
  cnpj_empresa: contrato.cnpj_empresa || '37395997000136',
  endereco_empresa: contrato.endereco_empresa || 'R. Belmira Isabel Martins',
  numero_empresa: contrato.numero_empresa || '42',
  bairro_empresa: contrato.bairro_empresa || 'Estreito',
  cidade_empresa: contrato.cidade_empresa || 'Florianópolis',
  estado_empresa: contrato.estado_empresa || 'SC',
  cep_empresa: contrato.cep_empresa || '88075145',
  representante_legal_empresa: contrato.representante_legal_empresa || 'Rafael Silva Teixeira',
  cpf_representante_empresa: contrato.cpf_representante_empresa || '08840379916',
  cargo_representante_empresa: contrato.cargo_representante_empresa || 'Diretor Eng.',
  kit_forma_pagamento: contrato.kit_forma_pagamento || '',
  kit_valor_pagamento: contrato.kit_valor_pagamento || contrato.valor_kit || 0,
  kit_numero_parcelas: contrato.kit_numero_parcelas || 1,
  kit_valor_parcela: contrato.kit_valor_parcela || 0,
  kit_observacoes: contrato.kit_observacoes || '',
  integracao_modalidade_pagamento: contrato.integracao_modalidade_pagamento || 1,
  integracao_forma_pagamento: contrato.integracao_forma_pagamento || '',
  integracao_valor_total: contrato.integracao_valor_total || contrato.valor_integracao || 0,
  integracao_numero_parcelas: contrato.integracao_numero_parcelas || 1,
  integracao_valor_parcela: contrato.integracao_valor_parcela || 0,
  integracao_observacoes: contrato.integracao_observacoes || '',
  integracao_percentual_assinatura: contrato.integracao_percentual_assinatura || 100,
  integracao_percentual_inicio_obra: contrato.integracao_percentual_inicio_obra || 0,
  integracao_percentual_conclusao_obra: contrato.integracao_percentual_conclusao_obra || 0,
  integracao_percentual_ligacao_definitiva: contrato.integracao_percentual_ligacao_definitiva || 0,
  integracao_valor_assinatura: contrato.integracao_valor_assinatura || 0,
  integracao_valor_inicio_obra: contrato.integracao_valor_inicio_obra || 0,
  integracao_valor_conclusao_obra: contrato.integracao_valor_conclusao_obra || 0,
  integracao_valor_ligacao_definitiva: contrato.integracao_valor_ligacao_definitiva || 0,
  integracao_forma_pagamento_assinatura: contrato.integracao_forma_pagamento_assinatura || contrato.integracao_forma_pagamento || '',
  integracao_forma_pagamento_inicio_obra: contrato.integracao_forma_pagamento_inicio_obra || '',
  integracao_forma_pagamento_conclusao_obra: contrato.integracao_forma_pagamento_conclusao_obra || contrato.integracao_forma_pagamento || '',
  integracao_forma_pagamento_ligacao_definitiva: contrato.integracao_forma_pagamento_ligacao_definitiva || '',
  integracao_numero_parcelas_assinatura: contrato.integracao_numero_parcelas_assinatura || 1,
  integracao_valor_parcela_assinatura: contrato.integracao_valor_parcela_assinatura || 0,
  integracao_numero_parcelas_inicio_obra: contrato.integracao_numero_parcelas_inicio_obra || 1,
  integracao_valor_parcela_inicio_obra: contrato.integracao_valor_parcela_inicio_obra || 0,
  integracao_numero_parcelas_conclusao_obra: contrato.integracao_numero_parcelas_conclusao_obra || 1,
  integracao_valor_parcela_conclusao_obra: contrato.integracao_valor_parcela_conclusao_obra || 0,
  integracao_numero_parcelas_ligacao_definitiva: contrato.integracao_numero_parcelas_ligacao_definitiva || 1,
  integracao_valor_parcela_ligacao_definitiva: contrato.integracao_valor_parcela_ligacao_definitiva || 0
});

// Função para detectar se houve mudanças nos dados do contrato
export const detectarMudancasContrato = (
  dadosOriginais: DadosContrato,
  dadosAtuais: DadosContrato
): boolean => {
  // Comparar todos os campos relevantes
  const camposParaComparar: (keyof DadosContrato)[] = [
    'valor_kit', 'valor_integracao', 'valor_total',
    'nome_cliente', 'cpf', 'estado_civil', 'profissao',
    'razao_social', 'cnpj', 'representante_legal', 'cpf_representante',
    'endereco', 'numero', 'bairro', 'cidade', 'estado', 'cep',
    'razao_social_empresa', 'nome_fantasia_empresa', 'cnpj_empresa',
    'endereco_empresa', 'numero_empresa', 'bairro_empresa', 
    'cidade_empresa', 'estado_empresa', 'cep_empresa',
    'representante_legal_empresa', 'cpf_representante_empresa', 'cargo_representante_empresa',
    'kit_forma_pagamento', 'kit_valor_pagamento', 'kit_numero_parcelas', 
    'kit_valor_parcela', 'kit_observacoes',
    'integracao_modalidade_pagamento', 'integracao_forma_pagamento', 
    'integracao_valor_total', 'integracao_numero_parcelas', 
    'integracao_valor_parcela', 'integracao_observacoes',
    'integracao_percentual_assinatura', 'integracao_percentual_inicio_obra',
    'integracao_percentual_conclusao_obra', 'integracao_percentual_ligacao_definitiva',
    'integracao_valor_assinatura', 'integracao_valor_inicio_obra',
    'integracao_valor_conclusao_obra', 'integracao_valor_ligacao_definitiva',
    'integracao_forma_pagamento_assinatura', 'integracao_forma_pagamento_inicio_obra',
    'integracao_forma_pagamento_conclusao_obra', 'integracao_forma_pagamento_ligacao_definitiva'
  ];

  for (const campo of camposParaComparar) {
    const valorOriginal = dadosOriginais[campo];
    const valorAtual = dadosAtuais[campo];
    
    // Comparação especial para números (considerar diferenças de arredondamento)
    if (typeof valorOriginal === 'number' && typeof valorAtual === 'number') {
      if (Math.abs(valorOriginal - valorAtual) > 0.01) {
        return true;
      }
    } else if (valorOriginal !== valorAtual) {
      return true;
    }
  }

  return false;
};
